import React, { useEffect, useState, useCallback, useRef } from "react";

// import CreatableSelect from "react-select/creatable";

import Select from "react-select";
import AsyncSelect from "react-select/async";

import { FieldProps, getIn } from "formik";
import { FormGroup } from "react-bootstrap";
import ErrorMessage from "src/views/Docuclipper/ErrorMessage";

interface Option {
  readonly label: string;
  readonly value: string;
}

type Props = {
  type:
    | "customer"
    | "vendor"
    | "account"
    | "category"
    | "service"
    | "tax"
    | "integration";
  value: Option | null;
  createFn?: (newValue: string) => Promise<{ name: string; id: string }>;
  defaultOptions: Option[];
  onChange: (option: any) => any;
  isInvalid?: boolean;
  loadOptions?: (inputValue: string) => Promise<Option[]>;
};

export const QboSelect: React.FC<Props> = ({
  createFn,
  defaultOptions,
  onChange,
  type,
  value,
  isInvalid,
  loadOptions,
  ...rest
}) => {
  // const [isLoading, setIsLoading] = useState(false);
  const [options, setOptions] = useState(defaultOptions);
  const [selectedValue, setSelectedValue] = useState<Option | null>(value);

  useEffect(() => {
    setSelectedValue(value);
  }, [value]);

  useEffect(() => {
    setOptions(defaultOptions);
  }, [defaultOptions]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (loadOptionsTimeoutRef.current) {
        clearTimeout(loadOptionsTimeoutRef.current);
      }
    };
  }, []);

  // const handleCreate = (inputValue: string) => {
  //   setIsLoading(true);

  //   createFn(inputValue)
  //     .then((rsp) => {
  //       const newOption = { label: rsp.name, value: rsp.id };
  //       setIsLoading(false);
  //       setOptions((prev) => [...prev, newOption]);
  //       setSelectedValue(newOption);
  //     })
  //     .catch((err) => {
  //       setIsLoading(false);
  //     });
  // };

  const commonStyles = {
    control: (base, state) => ({
      ...base,
      border: isInvalid ? "1px solid #e55353 !important" : "1px solid #ced4da",
      boxShadow: "none",
      "&:hover": {
        border: isInvalid
          ? "1px solid #e55353 !important"
          : "1px solid #ced4da",
      },
    }),
    menu: (provided) => ({
      ...provided,
      zIndex: 9999, // Add this line to increase the z-index of the dropdown
    }),
  };

  // Use ref to track the debounce timeout
  const loadOptionsTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Create debounced version of loadOptions that returns a Promise
  const debouncedLoadOptions = useCallback(
    (inputValue: string): Promise<Option[]> => {
      return new Promise((resolve) => {
        // Clear any existing timeout
        if (loadOptionsTimeoutRef.current) {
          clearTimeout(loadOptionsTimeoutRef.current);
        }

        if (!loadOptions) {
          resolve([]);
          return;
        }

        // If input is null/undefined or too short, return default options or empty array
        if (!inputValue || inputValue.length < 2) {
          resolve(defaultOptions ? defaultOptions.slice(0, 50) : []); // Return first 50 default options
          return;
        }

        // Set new timeout
        loadOptionsTimeoutRef.current = setTimeout(async () => {
          try {
            const options = await loadOptions(inputValue);
            resolve(options);
          } catch (error) {
            console.error("Error loading options:", error);
            resolve([]);
          }
        }, 400); // 400ms debounce delay
      });
    },
    [loadOptions, defaultOptions]
  );

  // Use AsyncSelect if loadOptions is provided, otherwise use regular Select
  if (loadOptions) {
    return (
      <AsyncSelect
        placeholder="Type to search"
        {...rest}
        isClearable
        cacheOptions
        defaultOptions={defaultOptions}
        loadOptions={debouncedLoadOptions}
        onChange={(newValue) => {
          setSelectedValue(newValue ? newValue : null);
          onChange(newValue ? newValue.value : null);
        }}
        value={selectedValue}
        styles={commonStyles}
        noOptionsMessage={({ inputValue }) =>
          !inputValue || inputValue.length < 2
            ? "Type at least 2 characters to search"
            : "No options found"
        }
      />
    );
  }

  return (
    // <CreatableSelect
    //   placeholder="Type to create a new item"
    //   {...rest}
    //   isClearable
    //   isDisabled={isLoading}
    //   isLoading={isLoading}
    //   onChange={(newValue) => {
    //     setSelectedValue(newValue ? newValue : null);
    //     onChange(newValue ? newValue.value : null);
    //   }}
    //   onCreateOption={handleCreate}
    //   options={options}
    //   value={selectedValue}
    //   formatCreateLabel={(val) => `Create ${type} "${val}"`}
    //   styles={{
    //     control: (base, state) => ({
    //       ...base,
    //       border: isInvalid ? '1px solid #e55353 !important' : '1px solid #ced4da',
    //       boxShadow: 'none',
    //       '&:hover': {
    //         border: isInvalid ? '1px solid #e55353 !important' : '1px solid #ced4da',
    //       }
    //     })
    //   }}
    // />
    <Select
      placeholder="Type to search an item"
      {...rest}
      isClearable
      onChange={(newValue) => {
        setSelectedValue(newValue ? newValue : null);
        onChange(newValue ? newValue.value : null);
      }}
      options={options}
      value={selectedValue}
      styles={commonStyles}
    />
  );
};

export const FormikQboSelect = ({
  createFn,
  onChangeFn,
  type,
  field, // { name, value, onChange, onBlur }
  form: { touched, errors, setFieldValue }, // also values, setXXXX, handleXXXX, dirty, isValid, status, etc.
  value,
  loadOptions,
  ...props
}: FieldProps & {
  label: string;
  options: Array<{ value: string; label: string }>;
  createFn?: any;
  onChangeFn?: any;
  type:
    | "customer"
    | "vendor"
    | "account"
    | "category"
    | "service"
    | "tax"
    | "integration";
  value: any;
  style?: React.CSSProperties;
  dataCy?: string;
  loadOptions?: (
    inputValue: string
  ) => Promise<Array<{ value: string; label: string }>>;
}) => {
  const { options } = props;
  const isInvalid = getIn(touched, field.name) && getIn(errors, field.name);
  const error = getIn(errors, field.name);

  return (
    <FormGroup
      style={props.style ? props.style : {}}
      data-cy={props.dataCy ? props.dataCy : ""}
    >
      {props.label && <label htmlFor={field.name}>{props.label}</label>}
      <QboSelect
        {...field}
        {...props}
        defaultOptions={options}
        value={value}
        onChange={(option) => {
          setFieldValue(field.name, option ? option : "");
          if (onChangeFn) {
            onChangeFn(option);
          }
        }}
        createFn={createFn}
        type={type}
        isInvalid={!!isInvalid}
        loadOptions={loadOptions}
      />
      {isInvalid && <ErrorMessage>{error}</ErrorMessage>}
    </FormGroup>
  );
};
